import { useState, useEffect, useCallback } from "react";
import { Filter, ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from "lucide-react";
import api from "../lib/api";
import { usd, signed, fmtPrice, timeAgo, clsx, dirUp, dirLabel } from "../lib/format";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";

const ResultBadge = ({ status, result }) => {
  const label = status === "open" ? "OPEN" : result === "win" ? "WIN" : result === "loss" ? "LOSS" : (result || status).toUpperCase();
  const cls = status === "open" ? "bg-brandblue/15 text-brandblue border-brandblue/30"
    : result === "win" ? "bg-pos/15 text-pos border-pos/30"
    : result === "loss" ? "bg-neg/15 text-neg border-neg/30"
    : "bg-fx-s3/20 text-fx-s3 border-white/10";
  return <span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono font-semibold border", cls)}>{label}</span>;
};

export default function History() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ mode: "all", result: "all", status: "all", symbol: "all" });
  const [assets, setAssets] = useState([]);
  const [detail, setDetail] = useState(null);
  const limit = 15;

  useEffect(() => { api.get("/market/assets").then((r) => setAssets(r.data)); }, []);

  const load = useCallback(async () => {
    const params = { page, limit };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "all") params[k] = v; });
    const { data } = await api.get("/trades", { params });
    setItems(data.items);
    setTotal(data.total);
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const setF = (k) => (v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const pages = Math.max(1, Math.ceil(total / limit));

  const Sel = ({ k, label, opts }) => (
    <Select value={filters[k]} onValueChange={setF(k)}>
      <SelectTrigger data-testid={`filter-${k}`} className="h-9 w-[130px] bg-fx-s1 border-white/10 text-xs"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="bg-fx-s2 border-white/10 text-white">
        {opts.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-[1400px]">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Trading History</h1>
        <p className="text-fx-s3 mt-1">Every trade, demo and live, in one place.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-fx-s3" />
        <Sel k="mode" label="Account" opts={[["all", "All accounts"], ["demo", "Demo"], ["live", "Live"]]} />
        <Sel k="status" label="Status" opts={[["all", "All status"], ["open", "Open"], ["closed", "Closed"]]} />
        <Sel k="result" label="Result" opts={[["all", "All results"], ["win", "Win"], ["loss", "Loss"], ["pending", "Pending"]]} />
        <Sel k="symbol" label="Asset" opts={[["all", "All assets"], ...assets.map((a) => [a.symbol, a.symbol])]} />
      </div>

      <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-fx-s3 border-b border-white/5">
                <th className="px-4 py-3">Asset</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Direction</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Entry</th>
                <th className="px-4 py-3 text-right">Exit</th>
                <th className="px-4 py-3 text-right">P/L</th>
                <th className="px-4 py-3">Opened</th>
                <th className="px-4 py-3">Result</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-fx-s3">No trades match these filters.</td></tr>
              )}
              {items.map((t) => (
                <tr key={t.id} onClick={() => setDetail(t)} data-testid={`history-row-${t.id}`}
                  className="border-b border-white/[0.03] hover:bg-white/[0.03] cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-semibold text-white">{t.symbol}</td>
                  <td className="px-4 py-3"><span className={clsx("text-[11px] font-mono uppercase", t.mode === "demo" ? "text-brandblue" : "text-gold")}>{t.mode}</span></td>
                  <td className="px-4 py-3">
                    <span className={clsx("inline-flex items-center gap-1 font-mono text-xs", dirUp(t.direction) ? "text-pos" : "text-neg")}>
                      {dirUp(t.direction) ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{dirLabel(t.direction)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{usd(t.amount)}</td>
                  <td className="px-4 py-3 text-right font-mono text-fx-s3">{fmtPrice(t.entry_price)}</td>
                  <td className="px-4 py-3 text-right font-mono text-fx-s3">{t.exit_price ? fmtPrice(t.exit_price) : "-"}</td>
                  <td className={clsx("px-4 py-3 text-right font-mono font-semibold", (t.pnl ?? 0) > 0 ? "text-pos" : (t.pnl ?? 0) < 0 ? "text-neg" : "text-fx-s3")}>{t.pnl != null ? signed(t.pnl) : "-"}</td>
                  <td className="px-4 py-3 text-fx-s3 text-xs">{timeAgo(t.opened_at)}</td>
                  <td className="px-4 py-3"><ResultBadge status={t.status} result={t.result} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
          <span className="text-xs text-fx-s3 font-mono">{total} trades · page {page}/{pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8 border-white/10 bg-fx-base" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8 border-white/10 bg-fx-base" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="trade-detail-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2">{detail?.symbol} · <span className={dirUp(detail?.direction) ? "text-pos" : "text-neg"}>{dirLabel(detail?.direction)}</span></DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-fx-s3">Trade ID</span><span className="font-mono text-xs">{detail.id}</span></div>
              <div className="flex justify-between"><span className="text-fx-s3">Account</span><span className={detail.mode === "demo" ? "text-brandblue" : "text-gold"}>{detail.mode.toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-fx-s3">Status</span><ResultBadge status={detail.status} result={detail.result} /></div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
                <div><div className="text-fx-s3 text-xs">Investment</div><div className="font-mono">{usd(detail.amount)}</div></div>
                <div><div className="text-fx-s3 text-xs">Expected return</div><div className="font-mono text-pos">+{usd(detail.expected_return)}</div></div>
                <div><div className="text-fx-s3 text-xs">Entry price</div><div className="font-mono">{fmtPrice(detail.entry_price)}</div></div>
                <div><div className="text-fx-s3 text-xs">Exit price</div><div className="font-mono">{detail.exit_price ? fmtPrice(detail.exit_price) : "-"}</div></div>
                <div><div className="text-fx-s3 text-xs">Duration</div><div className="font-mono">{detail.duration}s</div></div>
                <div><div className="text-fx-s3 text-xs">P/L</div><div className={clsx("font-mono font-semibold", (detail.pnl ?? 0) >= 0 ? "text-pos" : "text-neg")}>{detail.pnl != null ? signed(detail.pnl) : "-"}</div></div>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/5"><span className="text-fx-s3">Opened</span><span className="font-mono text-xs">{timeAgo(detail.opened_at)}</span></div>
              <div className="flex justify-between"><span className="text-fx-s3">Closed</span><span className="font-mono text-xs">{detail.closed_at ? timeAgo(detail.closed_at) : "-"}</span></div>
              {detail.mode === "demo" && <p className="text-[11px] text-fx-s3 pt-2 border-t border-white/5">Simulated trade — results are for practice only.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
